import React, { useState, useEffect, useMemo } from 'react';
import {
  CommentText,
  CommentMetadata,
  CommentGroup,
  CommentContent,
  CommentAvatar,
  CommentActions,
  CommentAction,
  CommentAuthor,
  Comment,
  Button,
  Icon,
  Form,
  FormTextArea,
} from 'semantic-ui-react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import AjaxController from '../system/ajaxController';
import './contract.css';

const Max2ContractComments = () => {
  // local State
  const [comments, setComments]               = useState([]);
  const [showAddComment, setShowAddComment]   = useState(false);
  const [newComment, setNewComment]           = useState('');
  const [editingComment, setEditingComment]   = useState(null);
  const [editedComment, setEditedComment]     = useState('');
  const [replyingTo, setReplyingTo]           = useState(null); // Track the comment being replied to

  // global state
  const backendUrl  = useSelector((state) => state.app.backend.baseProtocol + '://' + state.app.backend.url);
  const contractId  = useSelector((state) => state.app.contractId);
  const userDetails = useSelector((state) => state.app.user);

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();

  // Memoized AjaxController instance
  const api = useMemo(() => new AjaxController(backendUrl), [backendUrl]);

  // Fetch comments on component mount
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const data = await api.get(`/contract/${contractId}/comments`);
        setComments(data);
      } catch (error) {
        enqueueSnackbar('Fehler beim Laden der Kommentare', { variant: 'error' });
      }
    };

    fetchComments();
  }, [contractId, api, enqueueSnackbar]);

  // Add a new comment or reply
  const handleAddComment = async () => {
    try {
      const endpoint = replyingTo 
        ? `/contract/${contractId}/comment/${replyingTo}/reply` 
        : `/contract/${contractId}/comment`;
      const data = await api.post(endpoint, { text: newComment, author: userDetails.name });
      setComments(data.comments);
      setNewComment('');
      setShowAddComment(false);
      setReplyingTo(null);
      enqueueSnackbar('Kommentar erfolgreich hinzugefügt', { variant: 'success' });
    } catch {
      enqueueSnackbar('Fehler beim Hinzufügen des Kommentars', { variant: 'error' });
    }
  };

  // Edit an existing comment
  const handleEditComment = async (commentId) => {
    try {
      const data = await api.put(`/contract/${contractId}/comment/${commentId}`, { text: editedComment });
      setComments(data.comments);
      setEditingComment(null);
      setEditedComment('');
      enqueueSnackbar('Kommentar erfolgreich bearbeitet', { variant: 'success' });
    } catch {
      enqueueSnackbar('Fehler beim Bearbeiten des Kommentars', { variant: 'error' });
    }
  };

  // Delete a comment
  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/contract/${contractId}/comment/${commentId}`);
      setComments(comments.filter((comment) => comment.id !== commentId));
      enqueueSnackbar('Kommentar erfolgreich gelöscht', { variant: 'success' });
    } catch {
      enqueueSnackbar('Fehler beim Löschen des Kommentars', { variant: 'error' });
    }
  };

  return (
    <>
      <CommentGroup>
        {comments.map((comment) => (
          <Comment key={comment.id}>
            <CommentAvatar src="./resources/icons/user.png" />
            <CommentContent>
              <CommentAuthor as="a">{comment.author}</CommentAuthor>
              <CommentMetadata>
                <div>
                  {new Date(comment.updated_at).toLocaleString()}
                  {comment.updated_at !== comment.created_at && (
                    <span> (bearbeitet)</span>
                  )}
                </div>
              </CommentMetadata>
              {editingComment === comment.id ? (
                <Form>
                  <FormTextArea value={editedComment} onChange={(e) => setEditedComment(e.target.value)} />
                  <Button basic circular icon labelPosition="left" onClick={() => handleEditComment(comment.id)}><Icon name="check" /> Speichern</Button>
                  <Button basic circular icon labelPosition="left" onClick={() => setEditingComment(null)}><Icon name="times" /> Abbrechen</Button>
                </Form>
              ) : (
                <>
                  <CommentText>
                    {comment.content.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </CommentText>
                  <CommentActions>
                    {userDetails.name === comment.author && (
                      <>
                      <CommentAction onClick={() => { setEditingComment(comment.id); setEditedComment(comment.content); }}><Icon name='edit' title='Kommentar Bearbeitem' /></CommentAction>
                      <CommentAction onClick={() => handleDeleteComment(comment.id)}><Icon name='trash' title='Kommentar löschen' /></CommentAction>
                      </>
                    )}
                    <CommentAction onClick={() => { setReplyingTo(comment.id); setShowAddComment(true);}}><Icon name='reply' title='Auf Kommentar antworten' /></CommentAction>
                  </CommentActions>
                  {comment.replies && comment.replies.length > 0 && (
                    <CommentGroup>
                      {comment.replies.map((reply) => (
                        <Comment key={reply.id}>
                          <CommentAvatar src="./resources/icons/user.png" />
                          <CommentContent>
                            <CommentAuthor as="a">{reply.author}</CommentAuthor>
                            <CommentMetadata>
                              <div>
                                {new Date(reply.updated_at).toLocaleString()}
                                {reply.updated_at !== reply.created_at && (
                                  <span> (bearbeitet)</span>
                                )}
                              </div>
                            </CommentMetadata>
                            {editingComment === reply.id ? (
                              <Form>
                                <FormTextArea value={editedComment} onChange={(e) => setEditedComment(e.target.value)} />
                                <Button basic circular icon labelPosition="left" onClick={() => handleEditComment(reply.id)}><Icon name="check" /> Speichern</Button>
                                <Button basic circular icon labelPosition="left" onClick={() => setEditingComment(null)}><Icon name="times" /> Abbrechen</Button>
                              </Form>
                            ) : (
                              <>
                              <CommentText>
                                {reply.content.split('\n').map((line, index) => (
                                  <React.Fragment key={index}>
                                    {line}
                                    <br />
                                  </React.Fragment>
                                ))}
                              </CommentText>
                              {userDetails.name === reply.author && (
                                <CommentActions>
                                  <CommentAction onClick={() => { setEditingComment(reply.id); setEditedComment(reply.content);}}><Icon name='edit' title='Kommentar Bearbeitem' /></CommentAction>
                                  <CommentAction onClick={() => handleDeleteComment(reply.id)}><Icon name='trash' title='Kommentar löschen' /></CommentAction>
                                </CommentActions>
                              )}
                              </>
                            )}
                          </CommentContent>
                        </Comment>
                      ))}
                    </CommentGroup>
                  )}
                </>
              )}
            </CommentContent>
          </Comment>
        ))}
      </CommentGroup>


      {showAddComment ? (
        <>
          <Form>
            <FormTextArea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder={replyingTo ? "Antwort hinzufügen..." : "Neue Bemerkung..."}/>
          </Form>
          <Button basic circular icon labelPosition="left" floated="right" onClick={handleAddComment}><Icon name="check" />Übernehmen</Button>
          <Button basic circular icon labelPosition="left" floated="right" onClick={() => { setShowAddComment(false); setReplyingTo(null); }}><Icon name="times" />Abbrechen</Button>
        </>
      ) : (
        <Button basic circular icon labelPosition="left" floated="right" onClick={() => setShowAddComment(true)}><Icon name="add" />Bemerkung hinzufügen</Button>
      )}
      <br/>
      <br/>
    </>
  );
};

export default Max2ContractComments;
